import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import { GoogleDriveService } from '../../services/GoogleDriveService';
import type { GoogleDriveFile, GoogleDriveFolderBrowserProps } from '../../types/googleDrive';
import './GoogleDriveBrowser.css';

export const GoogleDriveFolderBrowser: React.FC<GoogleDriveFolderBrowserProps> = ({
  isOpen,
  onClose,
  onFilesSelected
}) => {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [folders, setFolders] = useState<GoogleDriveFile[]>([]);
  const [files, setFiles] = useState<GoogleDriveFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<GoogleDriveFile[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<GoogleDriveFile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GoogleDriveFile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'modified'>('name');

  const driveService = GoogleDriveService.getInstance();
  const searchTimeoutRef = React.useRef<number | undefined>(undefined);

  useEffect(() => {
    if (isOpen) {
      checkAuthentication();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isAuthenticated) {
      loadFolderContents(currentFolder);
    }
  }, [isAuthenticated, currentFolder]);

  const checkAuthentication = async () => {
    try {
      setLoading(true);
      await driveService.initialize();
      setIsAuthenticated(driveService.isAuthenticated());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await driveService.signIn();
      
      if (result.success) {
        setIsAuthenticated(true);
      } else {
        setError(result.error || 'Authentication failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const loadFolderContents = async (folderId: string | null) => {
    try {
      setLoading(true);
      setError(null);
      setSearchQuery('');
      setIsSearching(false);

      // フォルダ一覧を取得
      const folderList = await driveService.listFolders(folderId || undefined);
      setFolders(folderList);

      // ファイル一覧を取得
      if (folderId) {
        const fileList = await driveService.listFilesInFolder(folderId);
        setFiles(fileList);

        // パンくずリストを取得
        const breadcrumbList = await driveService.getFolderBreadcrumb(folderId);
        setBreadcrumb(breadcrumbList);
      } else {
        setFiles([]);
        setBreadcrumb([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folder contents');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setIsSearching(true);

      const options: any = {
        folderId: currentFolder || undefined
      };

      // フィルタータイプによるMIMEタイプ指定
      if (filterType !== 'all') {
        options.mimeType = filterType;
      }

      const result = await driveService.searchFiles(query, options);
      setSearchResults(result.files);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // デバウンス処理
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(query);
    }, 500);
  };

  const getDisplayFiles = () => {
    let displayFiles = isSearching ? searchResults : files;

    // ソート
    displayFiles = [...displayFiles].sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else {
        return new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime();
      }
    });

    return displayFiles;
  };

  const handleFolderClick = (folder: GoogleDriveFile) => {
    setCurrentFolder(folder.id);
    setSelectedFiles([]);
  };

  const handleFileToggle = (file: GoogleDriveFile) => {
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      } else {
        return [...prev, file];
      }
    });
  };

  // すべて選択
  const handleSelectAll = () => {
    const selectableFiles = getDisplayFiles().filter(
      file => file.mimeType !== GoogleDriveService.MIME_TYPES.FOLDER
    );
    setSelectedFiles(selectableFiles);
  };

  // 選択をクリア
  const handleClearSelection = () => {
    setSelectedFiles([]);
  };

  const handleBreadcrumbClick = (folder: GoogleDriveFile | null) => {
    setCurrentFolder(folder ? folder.id : null);
    setSelectedFiles([]);
  };

  const handleImport = () => {
    onFilesSelected(selectedFiles);
    onClose();
  };

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('googleDrive.title')}
      size="xl"
    >
      <div className="google-drive-browser">
        {!isAuthenticated ? (
          <div className="auth-section">
            <div className="auth-content">
              <h3>{t('googleDrive.connect')}</h3>
              <p>{t('googleDrive.description')}</p>
              <Button
                onClick={handleSignIn}
                disabled={loading}
                variant="primary"
              >
                {loading ? t('common.connecting') : t('googleDrive.signIn')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* 検索バーとフィルター */}
            <div className="search-filter-bar">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className="search-input"
                  placeholder={t('googleDrive.searchPlaceholder') || 'Search files...'}
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                />
                {searchQuery && (
                  <button
                    className="clear-search"
                    onClick={() => {
                      setSearchQuery('');
                      setIsSearching(false);
                      setSearchResults([]);
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="filter-controls">
                <select
                  className="filter-select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="all">{t('googleDrive.allTypes') || 'All types'}</option>
                  <option value={GoogleDriveService.MIME_TYPES.DOCUMENT}>{t('googleDrive.documents') || 'Documents'}</option>
                  <option value={GoogleDriveService.MIME_TYPES.SPREADSHEET}>{t('googleDrive.spreadsheets') || 'Spreadsheets'}</option>
                  <option value={GoogleDriveService.MIME_TYPES.PRESENTATION}>{t('googleDrive.presentations') || 'Presentations'}</option>
                  <option value={GoogleDriveService.MIME_TYPES.PDF}>PDF</option>
                </select>
                <select
                  className="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'modified')}
                >
                  <option value="name">{t('googleDrive.sortByName') || 'Sort by name'}</option>
                  <option value="modified">{t('googleDrive.sortByModified') || 'Sort by modified'}</option>
                </select>
              </div>
            </div>

            {/* パンくずリスト */}
            <div className="breadcrumb">
              <button
                className="breadcrumb-item"
                onClick={() => handleBreadcrumbClick(null)}
              >
                {t('googleDrive.myDrive') || 'My Drive'}
              </button>
              {breadcrumb.map((folder) => (
                <React.Fragment key={folder.id}>
                  <span className="breadcrumb-separator">/</span>
                  <button
                    className="breadcrumb-item"
                    onClick={() => handleBreadcrumbClick(folder)}
                  >
                    {folder.name}
                  </button>
                </React.Fragment>
              ))}
              {isSearching && (
                <>
                  <span className="breadcrumb-separator">/</span>
                  <span className="breadcrumb-item search-indicator">
                    {t('googleDrive.searchResults') || 'Search results'}: "{searchQuery}"
                  </span>
                </>
              )}
            </div>

            {/* フォルダとファイル一覧 */}
            <div className="folder-contents">
              {loading ? (
                <div className="loading">{t('common.loading')}</div>
              ) : error ? (
                <div className="error">{error}</div>
              ) : (
                <>
                  {/* 検索中でない場合のみフォルダを表示 */}
                  {/* フォルダ一覧 */}
                  {!isSearching && folders.length > 0 && (
                    <div className="folder-section">
                      <h4>{t('common.folders')}</h4>
                      <div className="item-grid">
                        {folders.map((folder) => (
                          <div
                            key={folder.id}
                            className="folder-item"
                            onClick={() => handleFolderClick(folder)}
                          >
                            <div className={`item-icon ${driveService.getFileIconClass(folder.mimeType)}`}></div>
                            <div className="item-name">{folder.name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ファイル一覧 */}
                  {(getDisplayFiles().length > 0 || isSearching) && (
                    <div className="file-section">
                      <div className="file-section-header">
                        <h4>
                          {isSearching 
                            ? `${t('googleDrive.searchResults') || 'Search results'} (${getDisplayFiles().length})`
                            : `${t('common.files')} (${getDisplayFiles().length}${t('common.items')})`
                          }
                        </h4>
                        {getDisplayFiles().filter(f => f.mimeType !== GoogleDriveService.MIME_TYPES.FOLDER).length > 0 && (
                          <div className="file-section-actions">
                            <button
                              className="select-action-button"
                              onClick={handleSelectAll}
                              disabled={selectedFiles.length === getDisplayFiles().filter(f => f.mimeType !== GoogleDriveService.MIME_TYPES.FOLDER).length}
                            >
                              {t('googleDrive.selectAll') || 'すべて選択'}
                            </button>
                            <button
                              className="select-action-button"
                              onClick={handleClearSelection}
                              disabled={selectedFiles.length === 0}
                            >
                              {t('googleDrive.clearSelection') || '選択をクリア'}
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="file-list">
                        {getDisplayFiles().map((file) => {
                          const isFolder = file.mimeType === GoogleDriveService.MIME_TYPES.FOLDER;
                          
                          return (
                            <div
                              key={file.id}
                              className={`file-item ${selectedFiles.some(f => f.id === file.id) ? 'selected' : ''} ${isFolder ? 'folder-in-list' : ''}`}
                              onClick={() => isFolder ? handleFolderClick(file) : handleFileToggle(file)}
                            >
                              {!isFolder && (
                                <div className="file-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedFiles.some(f => f.id === file.id)}
                                    readOnly
                                  />
                                </div>
                              )}
                              <div className={`file-icon ${driveService.getFileIconClass(file.mimeType)}`}></div>
                              <div className="file-info">
                                <div className="file-name">{file.name}</div>
                                <div className="file-meta">
                                  {isFolder 
                                    ? t('common.folder') || 'Folder'
                                    : `${driveService.formatFileSize(file.size)} • ${driveService.formatModifiedTime(file.modifiedTime)}`
                                  }
                                </div>
                              </div>
                              {isFolder && (
                                <div className="folder-arrow">›</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {!isSearching && folders.length === 0 && files.length === 0 && (
                    <div className="empty-folder">
                      {t('googleDrive.emptyFolder') || 'This folder is empty'}
                    </div>
                  )}

                  {isSearching && getDisplayFiles().length === 0 && (
                    <div className="empty-folder">
                      {t('googleDrive.noSearchResults') || 'No files found matching your search'}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* アクションボタン */}
            <div className="action-buttons">
              <div className="selected-count">
                {selectedFiles.length}{t('common.items')}{t('googleDrive.filesSelected') || ' files selected'}
              </div>
              <div className="button-group">
                <Button variant="ghost" onClick={handleClose}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleImport}
                  disabled={selectedFiles.length === 0}
                >
                  {t('googleDrive.importSelected') || 'Import selected files'} ({selectedFiles.length})
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
